import React, { useEffect, useCallback } from "react";
import { useLoaderData } from "react-router-dom";
import { StationViewModel } from "@dining/api-client";
import { useStyles } from "./CafeViewStyles";
import { appInsightsEvents, trackEvent, useAppStore } from "../../common";
import { CafeViewLoaderData } from "./cafeViewLoader";
import { WeekdayPicker } from "./WeekdayPicker";
import { CafeData, StationList } from "./CafeData";
import { OrderList } from "../OrderView/OrderList/OrderList";
import { LoadingScreen, NoResultsMessage } from "../shared";
import { Button } from "@fluentui/react-components";
import { useNavigate } from "react-router-dom";

export const CafeView = () => {
  const styles = useStyles();
  const context = useAppStore((state) => state.context);
  const loading = useAppStore((state) => state.loading);
  const setLoading = useAppStore((state) => state.setLoading);
  const appInsights = useAppStore((state) => state.appInsights);
  const [setSelectedCategory] = useAppStore((state) => [state.menuList.setSelectedCategory]);

  // Data pulled from the loader in App.tsx
  const loaderData = useLoaderData() as CafeViewLoaderData;
  const stations: StationViewModel[] = loaderData.stations || [];
  const orders = useAppStore((state) => state.orders);
  // loader() is called whenever the props change,
  // so we only need to set loading to false when the station data changes,
  // since the prop changing means the date has changed in the WeekdayPicker.
  useEffect(() => {
    setLoading(false);
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "CafeView",
      cafeId: loaderData.cafe.id,
      cafeName: loaderData.cafe.name,
      stationCount: loaderData.stations.length,
    });
    document.title = `${loaderData.cafe.name} - Microsoft Dining`;
  }, [appInsights, context, loaderData]);

  useEffect(() => {
    // Cleanup function to clear selected value when the user navigates to the cafeview page
    return () => {
      setSelectedCategory("");
    };
  }, [setSelectedCategory]);

  const cafe = loaderData.cafe;
  const navigate = useNavigate();

  const showOrderHistory = useCallback(() => {
    navigate("/orders/past");
    trackEvent(appInsights, appInsightsEvents.Usage.Actions.NavigateToOrderHistory, context);
  }, [appInsights, context, navigate]);
  return (
    <div className={styles.container} data-testid="cafeView">
      <main className={styles.main}>
        <div className={styles.subcontainer}>
          {orders && orders.values && orders.values.length > 0 && (
            <div className={styles.orderListContainer}>
              <OrderList orderList={orders.values} />
            </div>
          )}
          <div className={styles.weekdayPickerContainer}>
            <div className={styles.weekdayRow}>
              <WeekdayPicker />
              <Button
                appearance="transparent"
                data-testid="changOrderHistory"
                aria-label="Order history"
                className={styles.orderButton}
                onClick={showOrderHistory}
                role="link"
              >
                View Order History & Reorder
              </Button>
            </div>
          </div>
        </div>
        <div className={styles.subMainContainer}>
          <CafeData cafeData={cafe} />
          {loading ? (
            <LoadingScreen />
          ) : stations.length > 0 ? (
            <StationList stations={stations} cafeId={cafe.id} />
          ) : (
            <NoResultsMessage
              primaryMessage="Check back soon!"
              secondaryMessage="Sorry, we don't have your local dining information for this date."
            />
          )}
        </div>
      </main>
    </div>
  );
};
